import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { api } from '../api/client';
import { useCart } from '../state/cart';
import { useAuth } from '../state/auth';
import { useExperiment } from '../state/experiment';
import { useT, useLocalField } from '../i18n';

const variantIcons = {
  softcover: '📖',
  hardcover: '📕',
  digital: '📱',
};

// Pay-first cart line: a book slot (template + variant, no story yet). Payment is
// for the whole cart at once via a single "pay for all" button below.
function SlotCard({ slot, onRemove }) {
  const t = useT();
  const lf = useLocalField();
  const icon = variantIcons[slot.variantType] || '📖';
  const variantLabel = t[slot.variantType] || slot.variantType;
  return (
    <div className="cart-item-card">
      <div className="cart-item-main">
        <div className="cart-item-icon">{icon}</div>
        <div className="cart-item-details">
          <div className="cart-item-title">{lf(slot, 'nameAr')}</div>
          <div className="cart-item-meta">
            <span className="cart-item-variant">{variantLabel}</span>
          </div>
        </div>
      </div>
      <div className="cart-item-end">
        <div className="cart-item-price">{((slot.priceCents || 0) / 100).toFixed(0)}{t.currency}</div>
        <button type="button" className="cart-remove" onClick={() => onRemove(slot.id)}>
          {t.removeFromCart}
        </button>
      </div>
    </div>
  );
}

// The pay-first cart: book slots + a single "pay for all" CTA. The actual stories
// are created afterwards from the paid order (see OrderDetail).
function PayFirstCart() {
  const t = useT();
  const { slots, removeSlot } = useCart();

  if (!slots || slots.length === 0) {
    return (
      <div className="cart-page">
        <h1 className="page-title">{t.cart}</h1>
        <div className="form-zone" style={{ textAlign: 'center' }}>
          <p className="empty-state">{t.cartEmpty}</p>
          <Link to="/" className="submit-btn">{t.backToHome}</Link>
        </div>
      </div>
    );
  }

  const totalCents = slots.reduce((sum, s) => sum + (s.priceCents || 0), 0);

  return (
    <div className="cart-page">
      <h1 className="page-title">{t.cart}</h1>
      <p className="cart-pending-hint">{t.payFirstCartHint}</p>
      <div className="cart-items-list">
        {slots.map((s) => (
          <SlotCard key={s.id} slot={s} onRemove={removeSlot} />
        ))}
      </div>
      <div className="cart-summary payfirst-summary">
        <div className="payfirst-reassure">
          <ShieldCheck size={20} aria-hidden="true" />
          <span>{t.payFirstReassurance}</span>
        </div>
        <div className="payfirst-summary-row">
          <div className="cart-total">
            <span>{t.total}</span>
            <strong>{(totalCents / 100).toFixed(0)}{t.currency}</strong>
          </div>
          <p className="cart-delivery-note">{t.payFirstDeliveryNote}</p>
          <Link to="/checkout" className="submit-btn cart-pay-btn cart-pay-all">
            {t.payFirstPayAll}
          </Link>
        </div>
      </div>
    </div>
  );
}

// Inline variant switcher (e.g. PDF → softcover/hardcover) for a cart line.
// Lazily loads the template's variants on first open, then PATCHes the chosen
// one to the server. onChanged receives the server response so the parent can
// refresh its price/type.
function VariantChanger({ shareToken, currentSku, currentType, onChanged }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [variants, setVariants] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const isCurrent = (v) => v.sku === currentSku || (!currentSku && v.type === currentType);

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    setError('');
    if (variants || loading) return;
    setLoading(true);
    try {
      const data = await api.get(`/api/stories/preview/${shareToken}`);
      setVariants((data.template?.variants || []).filter((v) => v.isActive));
    } catch {
      setError(t.cartVariantLoadError);
    } finally {
      setLoading(false);
    }
  };

  const pick = async (v) => {
    if (isCurrent(v)) { setOpen(false); return; }
    setBusy(true);
    setError('');
    try {
      const resp = await api.patch(`/api/stories/${shareToken}/variant`, { variantSku: v.sku });
      setOpen(false);
      onChanged(resp);
    } catch (e) {
      setError(e.message || t.cartVariantChangeError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cart-variant-changer">
      <button type="button" className="cart-change-link" onClick={toggle} disabled={busy}>
        {busy ? t.cartVariantSaving : open ? t.cartVariantClose : t.cartChangeVariant}
      </button>
      {open && (
        <div className="cart-variant-options">
          {loading && <span className="cart-variant-note">…</span>}
          {error && <span className="cart-variant-error">{error}</span>}
          {variants && variants.map((v) => (
            <button
              key={v.sku}
              type="button"
              className={`cart-variant-option ${isCurrent(v) ? 'is-current' : ''}`}
              onClick={() => pick(v)}
              disabled={busy}
            >
              <span>{variantIcons[v.type] || '📖'} {t[v.type] || v.type}</span>
              <span className="cart-variant-option-price">
                {v.compareAtCents > v.priceCents && (
                  <span className="price-was">{(v.compareAtCents / 100).toFixed(0)}{t.currency}</span>
                )}
                {(v.priceCents / 100).toFixed(0)}{t.currency}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CartItemCard({ item, onRemove, onVariantChanged }) {
  const t = useT();
  const lf = useLocalField();
  const icon = variantIcons[item.variantType] || '📖';
  const variantLabel = t[item.variantType] || item.variantType;
  const childName = item.personalization?.name;

  return (
    <div className="cart-item-card">
      <Link to={item.shareToken ? `/preview/${item.shareToken}` : '#'} className="cart-item-main">
        <div className="cart-item-icon">{icon}</div>
        <div className="cart-item-details">
          <div className="cart-item-title">{lf(item, 'nameAr')}</div>
          <div className="cart-item-meta">
            <span className="cart-item-variant">{variantLabel}</span>
            {childName && <span className="cart-item-child">{t.cartChildPrefix}{childName}</span>}
          </div>
        </div>
      </Link>
      <div className="cart-item-end">
        <div className="cart-item-price">{((item.priceCents || 0) / 100).toFixed(0)}{t.currency}</div>
        {item.shareToken && (
          <VariantChanger
            shareToken={item.shareToken}
            currentSku={item.variantSku}
            currentType={item.variantType}
            onChanged={(resp) => onVariantChanged(item, resp)}
          />
        )}
        {item.shareToken && (
          <Link to={`/checkout/${item.shareToken}`} className="submit-btn cart-pay-btn">
            {t.completePayment}
          </Link>
        )}
        <button type="button" className="cart-remove" onClick={() => onRemove(item.id)}>
          {t.removeFromCart}
        </button>
      </div>
    </div>
  );
}

// A pending (unpaid) order rendered as a cart line for logged-in users. Payment
// is per-order (each story has its own checkout via shareToken), so the CTA
// routes to that story's checkout rather than a single "pay all" button.
function PendingOrderCard({ order, onVariantChanged }) {
  const t = useT();
  const lf = useLocalField();
  const item = order.items?.[0] || {};
  const variantType = item.type;
  const icon = variantIcons[variantType] || '📖';
  const variantLabel = t[variantType] || variantType;
  const templateName = lf(item.templateId || {}, 'nameAr') || t.cartStoryFallback;
  const childName = item.storyId?.personalization?.name;
  const shareToken = item.storyId?.shareToken;

  return (
    <div className="cart-item-card">
      <Link to={shareToken ? `/preview/${shareToken}` : '#'} className="cart-item-main">
        <div className="cart-item-icon">{icon}</div>
        <div className="cart-item-details">
          <div className="cart-item-title">{templateName}</div>
          <div className="cart-item-meta">
            <span className="cart-item-variant">{variantLabel}</span>
            {childName && <span className="cart-item-child">{t.cartChildPrefix}{childName}</span>}
          </div>
        </div>
      </Link>
      <div className="cart-item-end">
        <div className="cart-item-price">{(order.totalCents / 100).toFixed(0)}{t.currency}</div>
        {shareToken && (
          <VariantChanger
            shareToken={shareToken}
            currentSku={item.variantSku}
            currentType={variantType}
            onChanged={onVariantChanged}
          />
        )}
        {shareToken && (
          <Link to={`/checkout/${shareToken}`} className="submit-btn cart-pay-btn">
            {t.completePayment}
          </Link>
        )}
      </div>
    </div>
  );
}

export default function Cart() {
  const t = useT();
  const { items, removeItem, pendingOrders, saveGuestItem, refreshPendingOrders } = useCart();
  const { user } = useAuth();
  const { payFirst } = useExperiment();

  // Pay-first variant: the cart is a list of book slots paid for together, with
  // the stories created afterwards. Entirely separate from the per-story cart.
  if (payFirst) return <PayFirstCart />;

  // Guest variant change: persist the new type/price into the localStorage line.
  const onGuestVariantChanged = (item, resp) => {
    if (!resp?.variant) return;
    saveGuestItem({
      ...item,
      variantSku: resp.variant.sku,
      variantType: resp.variant.type,
      priceCents: resp.variant.priceCents,
    });
  };

  // Logged-in variant change: the server already updated the pending order, so
  // just re-pull it to refresh the displayed type + price.
  const onPendingVariantChanged = () => { refreshPendingOrders(); };

  // Logged-in users: cart = their pending orders (server-backed).
  if (user) {
    if (pendingOrders.length === 0) {
      return (
        <div className="cart-page">
          <h1 className="page-title">{t.cart}</h1>
          <div className="form-zone" style={{ textAlign: 'center' }}>
            <p className="empty-state">{t.cartEmpty}</p>
            <Link to="/" className="submit-btn">{t.backToHome}</Link>
          </div>
        </div>
      );
    }

    const totalCents = pendingOrders.reduce((sum, o) => sum + (o.totalCents || 0), 0);

    return (
      <div className="cart-page">
        <h1 className="page-title">{t.cart}</h1>
        <p className="cart-pending-hint">{t.cartPendingHint}</p>
        <div className="cart-items-list">
          {pendingOrders.map((o) => (
            <PendingOrderCard key={o._id} order={o} onVariantChanged={onPendingVariantChanged} />
          ))}
        </div>
        <div className="cart-summary">
          <div className="cart-total">
            <span>{t.total}</span>
            <strong>{(totalCents / 100).toFixed(0)}{t.currency}</strong>
          </div>
        </div>
      </div>
    );
  }

  // Guests: legacy localStorage cart.
  if (items.length === 0) {
    return (
      <div className="cart-page">
        <h1 className="page-title">{t.cart}</h1>
        <div className="form-zone" style={{ textAlign: 'center' }}>
          <p className="empty-state">{t.cartEmpty}</p>
          <Link to="/" className="submit-btn">{t.backToHome}</Link>
        </div>
      </div>
    );
  }

  const totalCents = items.reduce((sum, i) => sum + (i.priceCents || 0), 0);

  return (
    <div className="cart-page">
      <h1 className="page-title">{t.cart}</h1>
      <div className="cart-items-list">
        {items.map((item) => (
          <CartItemCard
            key={item.id}
            item={item}
            onRemove={removeItem}
            onVariantChanged={onGuestVariantChanged}
          />
        ))}
      </div>
      <div className="cart-summary">
        <div className="cart-total">
          <span>{t.total}</span>
          <strong>{(totalCents / 100).toFixed(0)}{t.currency}</strong>
        </div>
      </div>
    </div>
  );
}
